import { MAX_TICKS_PER_BEAT, SLOTS_PER_BEAT, PATTERN_SLOTS, LEVEL_NORMAL, LEVEL_ACCENT } from './constants';

/**
 * Positional step-slot stride - mirror of StepGrid.kt / the C++ engine.
 * A beat's ticks map to its 8 pattern slots at this stride so levels live at
 * musical positions: uniform even subdivisions nest (the "&" is slot 4 at
 * 1/8, 1/16 and 1/32 alike), grouped meters use a 16th-granular grid
 * (eighth t -> slot 2t in both 8ths and 16ths modes). Tick counts that don't
 * divide the grid (triplets, 5/6/7-lets, oversize groups) fall back to index
 * mapping.
 */
export function stepStride(units: number, ticks: number): number {
  if (ticks <= 1) return MAX_TICKS_PER_BEAT; // only t=0 exists -> slot 0
  let s = units <= 1
    ? (MAX_TICKS_PER_BEAT % ticks === 0 ? MAX_TICKS_PER_BEAT / ticks : 1)
    : (ticks === units ? 2 : 1);
  if ((ticks - 1) * s > MAX_TICKS_PER_BEAT - 1) s = 1; // stay inside the beat
  return s;
}

/** Pattern slot of tick `tick` in beat `beat` under the given beat structure. */
export function stepSlot(beat: number, tick: number, units: number, ticks: number): number {
  return beat * SLOTS_PER_BEAT + tick * stepStride(units, ticks);
}

/** Default pattern: accent on the downbeat, normal everywhere else. */
export function defaultStepPattern(): Uint8Array {
  const p = new Uint8Array(PATTERN_SLOTS).fill(LEVEL_NORMAL);
  p[0] = LEVEL_ACCENT;
  return p;
}

/** The app's serialized form: one digit (0..3) per slot, 128 chars. */
export function patternToString(p: Uint8Array): string {
  let s = '';
  for (let i = 0; i < PATTERN_SLOTS; i++) s += String(p[i] & 3);
  return s;
}

export function patternFromString(s: string | null | undefined): Uint8Array {
  if (!s || s.length !== PATTERN_SLOTS || !/^[0-3]+$/.test(s)) return defaultStepPattern();
  const p = new Uint8Array(PATTERN_SLOTS);
  for (let i = 0; i < PATTERN_SLOTS; i++) p[i] = s.charCodeAt(i) - 48;
  return p;
}

/**
 * Packs beats into grid rows by cell count (mirror of StepGrid.packBeatRows).
 * `cells` is each beat's cell (tick) count. Rows hold at most `capacity`
 * cells; the row count is the minimum that capacity allows, and the
 * effective capacity then grows until the greedy split fits that row count,
 * which keeps rows balanced instead of front-loaded. Returns rows of beat
 * indices, in order.
 */
export function packBeatRows(cells: number[], capacity = 16): number[][] {
  if (cells.length === 0) return [];
  const total = cells.reduce((a, b) => a + b, 0);
  const maxRows = Math.ceil(total / capacity);
  const pack = (cap: number): number[][] => {
    const rows: number[][] = [];
    let row: number[] = [];
    let count = 0;
    cells.forEach((c, beat) => {
      if (row.length && count + c > cap) {
        rows.push(row);
        row = [];
        count = 0;
      }
      row.push(beat);
      count += c;
    });
    rows.push(row);
    return rows;
  };
  let cap = Math.ceil(total / maxRows);
  let rows = pack(cap);
  while (rows.length > maxRows) rows = pack(++cap);
  return rows;
}
